<template>
  <span
    class="brand-logo"
    :class="['brand-logo--' + size, 'brand-logo--' + brandKey]"
    :title="brand.label"
  >
    <span class="brand-logo__plate" :class="{ 'brand-logo__plate--text': imgFailed || !hasImage }">
      <img
        v-if="hasImage && !imgFailed"
        :src="imageSrc"
        :alt="brand.label"
        class="brand-logo__img"
        loading="lazy"
        decoding="async"
        @error="imgFailed = true"
      />
      <span
        v-else
        class="brand-logo__mark"
        :style="markStyle"
      >{{ brand.mark }}</span>
    </span>
    <span v-if="showLabel" class="brand-logo__label">{{ brand.label }}</span>
  </span>
</template>

<script>
const BRAND_FILES = {
  apelsin:    'apelsin.png',
  humo:       'humo.jpg',
  infin:      'infin.ico',
  ipak:       'ipak.png',
  oson:       'oson.png',
  payme:      'payme.png',
  uzcard:     'uzcard.png',
  zoomrad:    'zoomrad.png',
  visa:       'visa.png',
  mastercard: 'mastercard.svg',
  telegram:   'telegram.svg',
};

const BRANDS = {
  uzcard:     { label: 'Uzcard',     mark: 'UZ',  bg: '#0F2545', fg: '#FFFFFF' },
  humo:       { label: 'Humo',       mark: 'H',   bg: '#28BC6F', fg: '#FFFFFF' },
  visa:       { label: 'Visa',       mark: 'V',   bg: '#1A1F71', fg: '#F7B600' },
  mastercard: { label: 'Mastercard', mark: 'MC',  bg: '#000000', fg: '#FFFFFF' },

  apelsin:    { label: 'Apelsin',    mark: 'A',   bg: '#7F4DFF', fg: '#FFFFFF' },
  quant:      { label: 'Quant',      mark: 'Q',   bg: '#F4D721', fg: '#1A1A1A' },
  ipak:       { label: 'Ipak',       mark: 'I',   bg: '#1F8F3C', fg: '#FFFFFF' },
  zoomrad:    { label: 'Zoomrad',    mark: 'Z',   bg: '#00B6D7', fg: '#FFFFFF' },
  infin:      { label: 'Infin',      mark: 'In',  bg: '#E5097F', fg: '#FFFFFF' },
  oson:       { label: 'Oson',       mark: 'O',   bg: '#E60023', fg: '#FFFFFF' },
  payme:      { label: 'Payme',      mark: 'P',   bg: '#22C8E5', fg: '#FFFFFF' },
  mavrid:     { label: 'Mavrid',     mark: 'M',   bg: '#FF6F3C', fg: '#FFFFFF' },
  telegram:   { label: 'Telegram',   mark: 'TG',  bg: '#229ED9', fg: '#FFFFFF' },

  unknown:    { label: 'Карта',      mark: '••',  bg: '#8898AA', fg: '#FFFFFF' },
};

export default {
  name: 'BrandLogo',
  props: {
    type: { type: String, default: '' },
    size: { type: String, default: 'md' },
    showLabel: { type: Boolean, default: false },
  },
  data() {
    return { imgFailed: false };
  },
  computed: {
    brandKey() {
      const k = String(this.type || '').toLowerCase().split('_')[0];
      return BRANDS[k] ? k : 'unknown';
    },
    brand() {
      return BRANDS[this.brandKey];
    },
    hasImage() {
      return !!BRAND_FILES[this.brandKey];
    },
    imageSrc() {
      const file = BRAND_FILES[this.brandKey];
      if (!file) return '';
      const base = process.env.BASE_URL || '/';
      return base + 'img/icons/providers/' + file;
    },
    markStyle() {
      return { background: this.brand.bg, color: this.brand.fg };
    },
  },
  watch: {
    type() { this.imgFailed = false; },
  },
};
</script>

<style scoped lang="scss">
.brand-logo {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  vertical-align: middle;
  font-family: 'Open Sans', sans-serif;

  &__plate {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #ffffff;
    border: 1px solid #e6e9f0;
    border-radius: 6px;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(50, 50, 93, 0.06);
    transition: box-shadow 0.18s ease, border-color 0.18s ease;
    flex-shrink: 0;
  }

  &:hover &__plate {
    border-color: #d0d6e3;
    box-shadow: 0 2px 6px rgba(50, 50, 93, 0.10);
  }

  &__img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }

  &__mark {
    width: 100%;
    height: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    letter-spacing: 0.3px;
    line-height: 1;
    border-radius: 4px;
  }

  &__label {
    font-size: 0.8rem;
    font-weight: 600;
    color: #525f7f;
  }

  // Sizes
  &--sm &__plate { width: 24px; height: 24px; padding: 2px; }
  &--sm &__mark  { font-size: 0.55rem; }

  &--md &__plate { width: 36px; height: 36px; padding: 4px; }
  &--md &__mark  { font-size: 0.7rem; }

  &--lg &__plate { width: 48px; height: 48px; padding: 5px; }
  &--lg &__mark  { font-size: 0.85rem; }

  // Text-fallback variants don't need the white plate background
  &__plate--text {
    background: transparent;
    border: none;
    box-shadow: none;
  }
}
</style>
